import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { RepairOutcome, RepairStatus, WarrantyType } from '@/lib/types';
import { cn } from '@/lib/utils';

export const REPAIR_STATUS: Record<RepairStatus, { label: string; className: string }> = {
  RECEIVED: { label: 'Angenommen', className: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  DIAGNOSIS: { label: 'Diagnose', className: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  QUOTE_PENDING: { label: 'KVA beim Kunden', className: 'bg-amber-500/20 text-amber-700 dark:text-amber-300' },
  APPROVED: { label: 'Freigegeben', className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  IN_REPAIR: { label: 'In Reparatur', className: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  WAITING_PARTS: { label: 'Warten auf Teile', className: 'bg-orange-500/15 text-orange-700 dark:text-orange-300' },
  TESTING: { label: 'Prüfung', className: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' },
  READY_FOR_PICKUP: { label: 'Abholbereit', className: 'bg-green-600/15 text-green-700 dark:text-green-300' },
  PICKED_UP: { label: 'Abgeholt', className: 'bg-muted text-muted-foreground' },
  CANCELLED: { label: 'Storniert', className: 'bg-muted text-muted-foreground line-through' },
};

export const OUTCOMES: Record<RepairOutcome, string> = {
  REPAIRED: 'Repariert',
  NO_FAULT_FOUND: 'Kein Fehler feststellbar',
  NOT_REPAIRABLE: 'Nicht reparabel',
  RETURNED_UNREPAIRED: 'Unrepariert zurück (z. B. KVA abgelehnt)',
};

export const WARRANTIES: Record<WarrantyType, string> = {
  NONE: 'Kostenpflichtig',
  WARRANTY: 'Herstellergarantie',
  STATUTORY: 'Gewährleistung',
  GOODWILL: 'Kulanz',
};

export const ACCESSORY_SUGGESTIONS = ['Netzteil', 'Ladekabel', 'Tasche', 'Hülle', 'Akku', 'Fernbedienung', 'Maus', 'Speicherkarte', 'SIM-Karte', 'Originalverpackung'];

export function RepairStatusBadge({ status, className }: { status: RepairStatus; className?: string }) {
  const s = REPAIR_STATUS[status];
  return <span className={cn('inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium', s.className, className)}>{s.label}</span>;
}

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
export const formatCents = (c: number) => euro.format(c / 100);

/** "12,50" → 1250; leer → null */
export function parseEuro(v: string): number | null {
  const t = v.trim().replace(/\s|€/g, '').replace(/\./g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

export const centsToInput = (c: number | null | undefined) => (c == null ? '' : (c / 100).toFixed(2).replace('.', ','));

export const DOCUMENTS = [
  { type: 'intake', title: 'Annahmeschein' },
  { type: 'label', title: 'Geräteetikett (62 × 29 mm)' },
  { type: 'workshop', title: 'Werkstattbegleitschein' },
  { type: 'quote', title: 'Kostenvoranschlag' },
  { type: 'report', title: 'Reparaturbericht' },
  { type: 'pickup', title: 'Abholbeleg' },
] as const;

export function DocumentsMenu({ repairId, available }: { repairId: string; available: string[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <FileText /> Belege
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {DOCUMENTS.filter((d) => available.includes(d.type)).map((d) => (
          <DropdownMenuItem key={d.type} asChild>
            <a href={`/api/repairs/${repairId}/documents/${d.type}`} target="_blank" rel="noreferrer">
              <FileText /> {d.title}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
